/**
 * Repository factory — builds the correct Knex adapter from a ModuleContext
 * and an EntityDefinition.
 *
 * Used by BaseEntityService to construct a default repo when none is injected.
 * Tests can bypass this entirely by passing a mock repo to the service constructor.
 */

import type { EntityDefinition, ModuleContext } from '@gzl10/nexus-sdk'
import type { EntityRepository } from './types.js'
import {
  KnexCollectionRepository,
  KnexSingleRepository,
  KnexTreeRepository,
  KnexDagRepository,
  KnexViewRepository,
} from './knex/index.js'

/**
 * Build a repository from ModuleContext + EntityDefinition.
 *
 * @throws Error if definition.type is unknown or definition has no table.
 */
export function createRepoFromCtx(
  ctx: ModuleContext,
  definition: EntityDefinition
): EntityRepository {
  const def = definition as Record<string, unknown>
  const adapter = def['adapter'] as string | undefined
  const knex = ctx.db.getKnex(adapter)
  const table = def['table'] as string | undefined

  if (!table) {
    throw new Error(`Entity definition has no table; type=${String(def['type'])}`)
  }

  const collectionOptions = {
    searchableFields: extractSearchableFields(def),
    defaultSort: (def['defaultSort'] as { field: string; order: 'asc' | 'desc' } | undefined) ?? null,
    labelField: def['labelField'] as string | undefined,
    fieldDefs: def['fields'] as Record<string, never> | undefined,
  }

  // Normalize deprecated type aliases (see CLAUDE.md: reference→collection, event→collection, temp→collection, external→collection, config→single, virtual→computed)
  const rawType = String(def['type'])
  const normalizedType =
    rawType === 'event' || rawType === 'reference' || rawType === 'temp' || rawType === 'external'
      ? 'collection'
      : rawType === 'config'
        ? 'single'
        : rawType

  switch (normalizedType) {
    case 'collection':
      return new KnexCollectionRepository(knex, table, collectionOptions)

    case 'single':
      return new KnexSingleRepository(knex, table, {
        scopeField: def['scopeField'] as string | undefined,
        idGenerator: () => ctx.core.generateId(),
      })

    case 'tree':
      return new KnexTreeRepository(knex, table, {
        ...collectionOptions,
        parentField: (def['parentField'] as string | undefined) ?? 'parent_id',
      })

    case 'dag':
      return new KnexDagRepository(knex, table, {
        ...collectionOptions,
        linkTable: (def['linkTable'] as string | undefined) ?? `${table}_links`,
        // Default 'child_id' matches KnexDagRepository convention.
        // Note: DagService (legacy) uses 'node_id' in its own _parents join table —
        // that table is managed by DagService directly, not by this repo.
        childField: (def['childField'] as string | undefined) ?? 'child_id',
        parentField: (def['parentField'] as string | undefined) ?? 'parent_id',
      })

    case 'view':
      return new KnexViewRepository(knex, table, collectionOptions)

    default:
      throw new Error(`Unknown entity type for repo factory: ${rawType}`)
  }
}

function extractSearchableFields(def: Record<string, unknown>): string[] {
  const fields = def['fields'] as Record<string, { meta?: { searchable?: boolean } }> | undefined
  if (!fields) return []
  return Object.entries(fields)
    .filter(([, f]) => f?.meta?.searchable === true)
    .map(([name]) => name)
}
